import React, { useMemo, useState } from "react";
import { FiCheckCircle, FiMail, FiSend, FiShield } from "react-icons/fi";

import Reveal from "./ui/Reveal";
import { subscribeNewsletter } from "../services/newsletterSubscriberServices";

function isValidEmail(email = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export default function HomeNewsletterSubscribe() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const cleanEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const canSubmit = isValidEmail(cleanEmail) && consent && status !== "loading";

  const handleSubmit = async (event) => {
    event.preventDefault();

    setMessage("");

    if (!isValidEmail(cleanEmail)) {
      setStatus("error");
      setMessage("Veuillez saisir une adresse email valide.");
      return;
    }

    if (!consent) {
      setStatus("error");
      setMessage("Veuillez accepter de recevoir les informations communales.");
      return;
    }

    setStatus("loading");

    try {
      await subscribeNewsletter(cleanEmail);

      setStatus("success");
      setMessage("Votre adresse email a bien été enregistrée.");
      setEmail("");
      setConsent(false);
    } catch (error) {
      const code = error?.response?.status;
      const apiMessage = error?.response?.data?.message;

      if (code === 409) {
        setStatus("info");
        setMessage("Cette adresse email est déjà inscrite.");
        return;
      }

      setStatus("error");
      setMessage(
        apiMessage ||
          "Une erreur est survenue pendant l'inscription. Veuillez réessayer.",
      );
    }
  };

  return (
    <section
      id="newsletter"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="newsletter-home-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white shadow-xl shadow-slate-950/5 dark:border-slate-800 dark:bg-slate-950">
            <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-emerald-300" />

            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#1277B9]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-1/3 h-52 w-52 rounded-full bg-emerald-400/10 blur-3xl" />

            <div className="relative grid gap-8 p-5 sm:p-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:p-8 xl:p-10">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                  Informations communales
                </div>

                <h2
                  id="newsletter-home-title"
                  className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
                >
                  Recevez les actualités de la mairie
                </h2>

                <p className="mt-4 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  Inscrivez votre adresse email pour recevoir les informations
                  communales, les actualités importantes, les alertes, les
                  événements et les newsletters de la mairie.
                </p>

                <div className="mt-6 flex items-start gap-3 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                    <FiShield className="h-5 w-5" />
                  </span>

                  <p>
                    Votre email sera utilisé uniquement pour les communications
                    de la mairie. Vous pourrez demander sa suppression à tout
                    moment auprès des services municipaux.
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="rounded-[2rem] border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 sm:p-5 md:p-6"
              >
                <label
                  htmlFor="newsletter-email"
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
                >
                  <FiMail className="h-4 w-4" />
                  Votre adresse email
                </label>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <FiMail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                    <input
                      id="newsletter-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="exemple@email.fr"
                      autoComplete="email"
                      className="h-13 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="group inline-flex min-h-13 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:min-w-[150px]"
                  >
                    {status === "loading" ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <FiSend className="h-4 w-4 transition group-hover:translate-x-0.5" />
                        M’inscrire
                      </>
                    )}
                  </button>
                </div>

                <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600 transition hover:border-[#1277B9]/30 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1277B9] focus:ring-[#1277B9]"
                  />

                  <span>
                    J’accepte de recevoir par email les informations communales,
                    newsletters et communications de la mairie.
                  </span>
                </label>

                {message && (
                  <div
                    className={[
                      "mt-4 rounded-2xl border px-4 py-3 text-sm font-semibold",
                      status === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                        : status === "info"
                          ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
                          : "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
                    ].join(" ")}
                    role="status"
                  >
                    <div className="flex items-start gap-2">
                      {status === "success" && (
                        <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      )}

                      <span>{message}</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}